// src/store/useTaskStore.js
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  subscribeToTasksInRange,
  fetchTasksInRange,
  taskCache
} from '@/services/taskApiService';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { createAdaptiveDebounce } from '@/utils/adaptiveDebounce';

const useTaskStore = create(
  devtools((set, get) => ({
    // Current view data (for Agenda view)
    currentTasks: [],
    currentCompletedTasks: [],
    currentLabels: [],
    currentView: 'Day',
    currentDate: new Date(),
    activeSubscription: null,
    timeOffset: null,
    userId: null,

    // Legacy data (for backward compatibilty)
    todayItems: [],
    allItems: [],

    // UI states
    loading: true, // Start with loading true for initial load
    error: null,

    // Active subscriptions
    legacySubscriptions: {},

    // Set current view and date
    setCurrentView: (view) => set({ currentView: view }),
    setCurrentDate: (date) => set({ currentDate: date }),
    setUserId: (userId) => set({ userId }),

    // Subscribe to tasks for current view
    subscribeToCurrentView: (userId, view, date) => {
      const state = get();

      // Defensive: fallback to defaults if undefined
      const safeView = view || state.currentView || 'Day';
      const safeDate = date || state.currentDate || new Date();

      // Unsubscribe from previous subscription
      if (state.activeSubscription) {
        state.activeSubscription();
      }

      // Store the userId for use in callbacks
      set({ userId });

      // Clear current tasks when switching views to prevent stale data
      set({ 
        loading: true, 
        error: null,
        currentTasks: [],
        currentCompletedTasks: [],
        currentLabels: []
      });

      // Check cache first
      const cacheKey = taskCache.createKey(userId, safeView, safeDate);
      const cached = taskCache.get(cacheKey);

      if (cached) {
        set({
          currentTasks: cached.items,
          currentCompletedTasks: cached.completedItems,
          currentLabels: cached.labels,
          loading: false
        });
      }

      // Set up real-time subscription
      const unsubscribe = subscribeToTasksInRange(
        userId,
        date,
        view,
        ({ items, completedItems, labels, error }) => {
          // Get the current state at callback time
          const currentState = get();
          // Re-introduce check to prevent stale updates from previous subscriptions
          if (currentState.currentView !== view || currentState.currentDate.getTime() !== date.getTime()) {
            return; // Ignore stale updates
          }

          if (error) {
            console.error('❌ [useTaskStore] Error in subscription:', error);
            set({ error, loading: false });
          } else {
            // Always update with the latest data - don't filter out "stale" updates
            // The subscription is already filtered by date/view in the query
            const cacheKey = taskCache.createKey(userId, view, date);
            taskCache.set(cacheKey, { items, completedItems, labels });

            set({
              currentTasks: items,
              currentCompletedTasks: completedItems,
              currentLabels: labels,
              loading: false,
              error: null
            });
          }
        }
      );

      set({ activeSubscription: unsubscribe });

      return unsubscribe;
    },

    getBrowserOffsetString: () => {
      const offsetMinutes = new Date().getTimezoneOffset(); // e.g., -330
      const absMinutes = Math.abs(offsetMinutes);
      const hours = Math.floor(absMinutes / 60);
      const minutes = absMinutes % 60;
      const sign = offsetMinutes <= 0 ? '+' : '-';
      return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    },

    fetchTimeOffset: async (userId) => {
      if (!userId) {
        console.error("No user available for offset fetch.");
        return;
      }

      const uid = userId;
      let offset = null;

      try {
        const docRef = doc(db, "notificationsSettings", uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const docData = docSnap.data();
          offset = docData.userTimezoneOffset || null;
        } else {
          console.error("No notificationSettings found for UID:", uid);
        }
      } catch (err) {
        console.error("Failed to fetch notificationSettings:", err);
      }


      if (!offset) {
        offset = get().getBrowserOffsetString();
      }

      set({ timeOffset: offset });
    },

    // Fetch tasks for any date/view (one-time)
    fetchTasksForDate: async (userId, date, viewType) => {
      try {
        const cacheKey = taskCache.createKey(userId, viewType, date);
        const cached = taskCache.get(cacheKey);

        if (cached) {
          return cached;
        }

        const result = await fetchTasksInRange(userId, date, viewType);
        taskCache.set(cacheKey, result);

        return result;
      } catch (error) {
        console.error('Error fetching tasks:', error);
        throw error;
      }
    },

    // Legacy methods for backward compatibility
    fetchToday: (userId) => {
      if (!userId) {
        set({ todayItems: [], todayLabels: [], completedItems: [], loading: false });
        return () => {};
      }

      set({ loading: true, error: null });

      const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

      // Query for uncompleted tasks
      const todayTasksQuery = query(
        collection(db, 'tasks'),
        where('userId', '==', userId),
        where('isCompleted', '==', false),
        where('date', '==', today)
      );

      // Query for completed tasks with today's date
      const todayCompletedTasksQuery = query(
        collection(db, 'tasks'),
        where('userId', '==', userId),
        where('isCompleted', '==', true),
        where('date', '==', today)  // Tasks that were due today
      );

      // Query for tasks completed today (different from due date)
      const completedTodayTasksQuery = query(
        collection(db, 'tasks'),
        where('userId', '==', userId),
        where('isCompleted', '==', true),
        where('CompletionDate', '==', today)
      );

      const todayApptsQuery = query(
        collection(db, 'appointments'),
        where('userId', '==', userId),
        where('date', '==', today)
      );

      let todayTasksData = [];
      let todayCompletedTasksData = [];
      let completedTodayTasksData = [];
      let todayApptsData = [];

      const processData = () => {
        try {
          const todayItems = [];
          const completedItems = [];

          // Add uncompleted tasks
          todayTasksData.forEach(doc => {
            todayItems.push({ id: doc.id, type: 'Tasks', ...doc.data() });
          });

          // Add completed tasks that were due today
          todayCompletedTasksData.forEach(doc => {
            const taskData = { id: doc.id, type: 'Tasks', ...doc.data() };
            // Only add if not already in the list
            if (!todayItems.find(item => item.id === doc.id)) {
              todayItems.push(taskData);
            }
          });

          // Add appointments
          todayApptsData.forEach((doc) => {
            todayItems.push({ id: doc.id, type: 'Appointments', ...doc.data() });
          });

          // Process tasks completed today (for the separate completed section)
          // Create a map to avoid duplicates
          const completedItemsMap = new Map();
          
          // First add tasks that were due today and completed
          todayCompletedTasksData.forEach(doc => {
            completedItemsMap.set(doc.id, { id: doc.id, type: 'Tasks', ...doc.data() });
          });
          
          // Then add all tasks completed today (regardless of original due date)
          completedTodayTasksData.forEach(doc => {
            completedItemsMap.set(doc.id, { id: doc.id, type: 'Tasks', ...doc.data() });
          });
          
          // Convert map to array
          completedItemsMap.forEach(item => {
            completedItems.push(item);
          });

          const todayLabels = [...new Set(todayItems.map(item => item.labels?.[0]))].filter(Boolean);

          // Only update state if data actually changed to prevent unnecessary re-renders
          // Performance note: JSON.stringify is used for deep comparison to prevent unnecessary state updates.
          // For large state objects, consider using a specialized deep-equality library (e.g., fast-deep-equal)
          // for better performance if this becomes a bottleneck.
          const currentState = get();
          const hasChanged =
            JSON.stringify(currentState.todayItems) !== JSON.stringify(todayItems) ||
            JSON.stringify(currentState.completedItems) !== JSON.stringify(completedItems);

          if (hasChanged || currentState.loading) {
            set({ todayItems, todayLabels, completedItems, loading: false, error: null });
          }
        } catch (error) {
          console.error('Error processing today data:', error);
          set({ error: error.message, loading: false });
        }
      };

      // Use adaptive debouncing to handle both single updates and bulk recurrent operations
      const debouncedProcessData = createAdaptiveDebounce(processData, {
        normalDelay: 1000,
        bulkDelay: 3000,
        rapidThreshold: 200,
        bulkThreshold: 4,
        debugLabel: 'useTaskStore.fetchTodayTasks'
      });

      const unsubTodayTasks = onSnapshot(todayTasksQuery,
        (snapshot) => {
          todayTasksData = snapshot.docs;
          debouncedProcessData();
        }
      );

      const unsubTodayCompletedTasks = onSnapshot(todayCompletedTasksQuery,
        (snapshot) => {
          todayCompletedTasksData = snapshot.docs;
          debouncedProcessData();
        }
      );

      const unsubTodayAppts = onSnapshot(todayApptsQuery,
        (snapshot) => {
          todayApptsData = snapshot.docs;
          debouncedProcessData();
        }
      );

      const unsubCompletedTasks = onSnapshot(completedTodayTasksQuery,
        (snapshot) => {
          completedTodayTasksData = snapshot.docs;
          debouncedProcessData();
        }
      );

      const unsubscribe = () => {
        unsubTodayTasks();
        unsubTodayCompletedTasks();
        unsubTodayAppts();
        unsubCompletedTasks();
      };

      set(state => ({
        legacySubscriptions: { ...state.legacySubscriptions, today: unsubscribe }
      }));

      return unsubscribe;
    },

    fetchAllTasks: (userId) => {
      if (!userId) {
        set({ allItems: [], allLabels: [], completedAllItems: [], loading: false });
        return () => {};
      }

      set({ loading: true, error: null });

      const allTasksQuery = query(
        collection(db, 'tasks'),
        where('userId', '==', userId),
        where('isCompleted', '==', false)
      );

      const allApptsQuery = query(
        collection(db, 'appointments'),
        where('userId', '==', userId)
      );

      const completedTasksQuery = query(
        collection(db, 'tasks'),
        where('userId', '==', userId),
        where('isCompleted', '==', true)
      );

      let allTasksData = [];
      let allApptsData = [];
      let completedTasksData = [];

      const processData = () => {
        try {
          const allItems = [];
          const completedAllItems = [];

          allTasksData.forEach(doc => {
            allItems.push({ id: doc.id, type: 'Tasks', ...doc.data() });
          });

          allApptsData.forEach((doc) => {
            allItems.push({ id: doc.id, type: 'Appointments', ...doc.data() });
          });

          completedTasksData.forEach(doc => {
            completedAllItems.push({ id: doc.id, type: 'Tasks', ...doc.data() });
          });

          const allLabels = [...new Set(allItems.map(item => item.labels?.[0]))].filter(Boolean);

          // Only update state if data actually changed to prevent unnecessary re-renders
          // Performance note: JSON.stringify is used for deep comparison to prevent unnecessary state updates.
          // For large state objects, consider using a specialized deep-equality library (e.g., fast-deep-equal)
          // for better performance if this becomes a bottleneck.
          const currentState = get();
          const hasChanged =
            JSON.stringify(currentState.allItems) !== JSON.stringify(allItems) ||
            JSON.stringify(currentState.completedAllItems) !== JSON.stringify(completedAllItems);

          if (hasChanged || currentState.loading) {
            set({ allItems, allLabels, completedAllItems, loading: false, error: null });
          }
        } catch (error) {
          console.error('Error processing all tasks data:', error);
          set({ error: error.message, loading: false });
        }
      };

      // Use adaptive debouncing to handle both single updates and bulk recurrent operations
      const debouncedProcessData = createAdaptiveDebounce(processData, {
        normalDelay: 1000,
        bulkDelay: 3000,
        rapidThreshold: 200,
        bulkThreshold: 4,
        debugLabel: 'useTaskStore.fetchAllTasks'
      });

      const unsubAllTasks = onSnapshot(allTasksQuery,
        (snapshot) => {
          allTasksData = snapshot.docs;
          debouncedProcessData();
        }
      );

      const unsubAllAppts = onSnapshot(allApptsQuery,
        (snapshot) => {
          allApptsData = snapshot.docs;
          debouncedProcessData();
        }
      );

      const unsubCompletedTasks = onSnapshot(completedTasksQuery,
        (snapshot) => {
          completedTasksData = snapshot.docs;
          debouncedProcessData();
        }
      );

      const unsubscribe = () => {
        unsubAllTasks();
        unsubAllAppts();
        unsubCompletedTasks();
      };

      set(state => ({
        legacySubscriptions: { ...state.legacySubscriptions, all: unsubscribe }
      }));

      return unsubscribe;
    },

    // CRUD operations 
    addTask: async (data) => {
        const collectionName = data.type.toLowerCase();
        try {
            set({ loading: true, error: null });

            // Ensure imageUrls and fileUrls are included in the data
            const taskData = {
                ...data,
                imageUrls: data.imageUrls || [],
                fileUrls: data.fileUrls || [],
                createdAt: new Date().toISOString()
            };

            const docRef = await addDoc(collection(db, collectionName), taskData);

            set(state => ({
                ...state,
                loading: false,
                error: null
            }));

            return { success: true, id: docRef.id };
        } catch (error) {
            set(state => ({
                ...state,
                loading: false,
                error: error.message
            }));
            return { success: false, error };
        }
    },

    markAsComplete: async (itemId) => {
      try {
        set({ loading: true, error: null });
        const itemRef = doc(db, "tasks", itemId);

        const completionDate = new Date().toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });

        await updateDoc(itemRef, {
          isCompleted: true,
          CompletionDate: completionDate,
          completionDateUTC: Timestamp.fromDate(new Date())
        });
        await get().deleteOrDoneNotification('DONE', 'Tasks', itemId);
        set({ loading: false });

        return { success: true };
      } catch (error) {
        set({ error: error.message, loading: false });
        return { success: false, error };
      }
    },

    reopenTask: async (itemId) => {
      try {
        set({ loading: true, error: null });
        const itemRef = doc(db, "tasks", itemId);

        await updateDoc(itemRef, {
          isCompleted: false,
          CompletionDate: ""
        });

        set({ loading: false });

        return { success: true };
      } catch (error) {
        set({ error: error.message, loading: false });
        return { success: false, error };
      }
    },

    deleteItem: async (itemId, itemType) => {
      try {
        set({ loading: true, error: null });

        await deleteDoc(doc(db, itemType.toLowerCase(), itemId));
        await get().deleteOrDoneNotification('DELETE', itemType, itemId);
        set({ loading: false });

        return { success: true };
      } catch (error) {
        console.error('Error deleting item:', error);
        set({ loading: false, error: error.message });
        return { success: false, error };
      }
    },

    updateItem: async (itemData, type) => {
        try {
            set({ loading: true, error: null });

            // Include UTC fields in allowed fields
            const taskFields = [
                'type_desc',
                'description',
                'description_md',  // Canonical markdown
                'description_format',  // Dual-write discriminator
                'date',
                'dueTime',
                'dueDateTimeUTC',  // Add this
                'type',
                'id',
                'priority',
                'labels',
                'notificationCustomDateTimeType',
                'imageUrls',
                'fileUrls'
            ];

            const appointmentFields = [
                'type_desc',
                'description',
                'description_md',  // Canonical markdown
                'description_format',  // Dual-write discriminator
                'date',
                'startTime',
                'endTime',
                'startDateTimeUTC',  // Add this
                'endDateTimeUTC',    // Add this
                'location',
                'type',
                'id',
                'labels',
                'notificationCustomDateTimeType',
                'imageUrls',
                'fileUrls'
            ];

            const allowedFields = type === 'Tasks' ? taskFields : appointmentFields;
            const cleanedData = Object.keys(itemData)
                .filter(key => allowedFields.includes(key))
                .reduce((obj, key) => {
                    obj[key] = itemData[key];
                    return obj;
                }, {});

            const collectionName = type.toLowerCase();
            const itemRef = doc(db, collectionName, itemData.id);

            await updateDoc(itemRef, cleanedData);

            set({ loading: false });

            return { success: true };
        } catch (error) {
            console.error(`Error updating ${type}:`, error);
            set({ error: error.message, loading: false });
            return { success: false, error };
        }
    },

    autoSaveUpdate: async (itemData, type) => {
      try {
          set({ loading: true, error: null });

          // Include UTC fields in allowed fields
          const taskFields = [
              'description',
              'description_md',
              'description_format'  // Dual-write discriminator
          ];

          const appointmentFields = [
              'description',
              'description_md',
              'description_format'  // Dual-write discriminator
          ];

          const noteFields = [
              'description',
              'description_md',
              'description_format'  // Dual-write discriminator
          ]

          const allowedFields = type === 'Tasks'
            ? taskFields
            : type === 'Appointments'
            ? appointmentFields
            : type === 'Notes'
            ? noteFields
            : [];
            
          const cleanedData = Object.keys(itemData)
              .filter(key => allowedFields.includes(key))
              .reduce((obj, key) => {
                  obj[key] = itemData[key];
                  return obj;
              }, {});

          const collectionName = type.toLowerCase();
          const itemRef = doc(db, collectionName, itemData.id);

          await updateDoc(itemRef, cleanedData);

          set({ loading: false });

          return { success: true };
      } catch (error) {
          console.error(`Error updating ${type}:`, error);
          set({ error: error.message, loading: false });
          return { success: false, error };
      }
    },

    addRecurrent: async (item) => {
      const collectionName = "recurrent_stage";
      try {
        set({ loading: true, error: null });

        const docRef = await addDoc(collection(db, collectionName), {
          ...item,
        });

        set(state => ({
          ...state,
          loading: false,
          error: null
        }));

        return { success: true, id: docRef.id };
      } catch (error) {
        set(state => ({
          ...state,
          loading: false,
          error: error.message
        }));
        return { success: false, error };
      }
    },

    addNotification: async (item) => {
      const collectionName = "notifications_stage";
      try {
        set({ loading: true, error: null });

        const docRef = await addDoc(collection(db, collectionName), {
          ...item,
        });

        set(state => ({
          ...state,
          loading: false,
          error: null
        }));

        return { success: true, id: docRef.id };
      } catch (error) {
        set(state => ({
          ...state,
          loading: false,
          error: error.message
        }));
        return { success: false, error };
      }
    },

    deleteOrDoneNotification: async (action, itemType, itemId) => {
      try {
        // Get current user ID from Firebase Auth
         const userId = get().userId;
        if (!userId) {
          console.error('No authenticated user found');
          return { success: false, error: 'User not authenticated' };
        }

        // Only proceed if it's Tasks or Appointments
        if (itemType !== 'Tasks' && itemType !== 'Appointments') {
          return { success: true, skipped: true };
        }

        const notificationData = {
          action: action, // 'DELETE' or 'DONE'
          type: itemType.toLowerCase().replace(/s$/, ''), // Convert "Tasks" to "task", "Appointments" to "appointment"
          id: itemId,
          userId: userId,
          timestamp: new Date().toISOString()
        };

        await addDoc(collection(db, 'notifications_stage'), notificationData);
        return { success: true };
      } catch (error) {
        console.error('Error sending notification stage:', error);
        return { success: false, error: error.message };
      }
    },
    
    fetchTasksByProject: async (projectName) => {
      try {
        set({ loading: true, error: null });
        
        const tasksQuery = query(
          collection(db, 'tasks'),
          where('labels', 'array-contains', projectName)
        );
        
        const snapshot = await getDocs(tasksQuery);
        const projectTasks = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: 'Tasks'
        }));
        
        // Update the tasks array with project tasks
        set(state => ({
          ...state,
          tasks: projectTasks,
          loading: false,
          error: null
        }));
        
        return projectTasks;
      } catch (error) {
        console.error('Error fetching project tasks:', error);
        set({ loading: false, error: error.message });
        return [];
      }
    },

    clearState: () => {
      const state = get();

      // Unsubscribe from active subscription
      if (state.activeSubscription) {
        state.activeSubscription();
      }

      // Unsubscribe from all legacy subscriptions
      Object.values(state.legacySubscriptions).forEach(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });

      // Clear cache
      taskCache.clear();

      set({
        currentTasks: [],
        currentCompletedTasks: [],
        currentLabels: [],
        todayItems: [],
        allItems: [],
        loading: false,
        error: null,
        activeSubscription: null,
        legacySubscriptions: {},
      });
    },
  }))
);

export default useTaskStore;
